import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Observable, interval, BehaviorSubject } from 'rxjs';
import { map, takeWhile } from 'rxjs/operators';
import { EnvService } from './env.service';

const STORE_KEY = 'ccsUserLastAction';

export enum SessionExpirationStatus {
  InitiateExpirationTimer = 'INITIATE_SESSION_EXPIRATION_TIMER',
  ResetExpirationTimer = 'RESET_SESSION_EXPIRATION_TIMER',
  StopExpirationTimer = 'STOP_SESSION_EXPIRATION_TIMER',
  InitiateWarningTimer = 'INITIATE_WARNING_TIMER',
  StartWarningTimer = 'START_WARNING_TIMER',
  StopWarningTimer = 'STOP_WARNING_TIMER',
}

@Injectable({ providedIn: 'root' })
export class SessionExpirationService implements OnDestroy {
  public static runTimer: boolean;
  public static runWarningTimer: boolean;
  public USER_IDLE_TIMER_VALUE_IN_MIN: number = 10;
  public WARNING_TIMER_VALUE_IN_MIN: number = 8;
  public userIdlenessChecker?: BehaviorSubject<string>;
  public idlenessWarningChecker?: BehaviorSubject<string>;
  public clockForIdle?: Observable<number>;

  private sessionForIdle?: Observable<number>;
  private userActivityChangeCallback: ($event: any) => void = () => {};

  constructor(private zone: NgZone, private envService: EnvService) {
    const { sessionDurationInMinutes, sessionExpirationWarningThresholdInMinutes } = this.envService.sessionExpiration;
    this.USER_IDLE_TIMER_VALUE_IN_MIN = sessionExpirationWarningThresholdInMinutes;
    this.WARNING_TIMER_VALUE_IN_MIN = sessionDurationInMinutes - sessionExpirationWarningThresholdInMinutes;
    this.userIdlenessChecker = new BehaviorSubject<string>(SessionExpirationStatus.InitiateExpirationTimer);
    this.idlenessWarningChecker = new BehaviorSubject<string>(SessionExpirationStatus.InitiateWarningTimer);
  }

  // Called on load to see if session is already expired
  public isSessionAlreadyExpired() {
    const now = Date.now();
    const validUntil = this.lastAction + this.envService.sessionExpiration.sessionDurationInMinutes * 60 * 1000;
    const diff = validUntil - now;
    return diff < 0;
  }

  public initilizeSessionTimeout(): void {
    SessionExpirationService.runTimer = true;
    this.reset();
    this.initListener();
    this.initInterval();
  }

  get lastAction(): number {
    return parseInt(localStorage.getItem(STORE_KEY) || Date.now().toString(), 10);
  }

  set lastAction(value) {
    localStorage.setItem(STORE_KEY, value.toString());
  }

  private initListener(): void {
    this.zone.runOutsideAngular(() => {
      this.userActivityChangeCallback = ($event) => this.handleUserActiveState($event);
      window.document.addEventListener('click', this.userActivityChangeCallback.bind(this), true);
    });
  }

  handleUserActiveState(_: any): void {
    this.reset();
  }

  public reset(): void {
    this.lastAction = Date.now();

    if (this.userIdlenessChecker) {
      this.userIdlenessChecker.next(SessionExpirationStatus.ResetExpirationTimer);
    }
  }

  private initInterval(): void {
    const intervalDuration = 1000;

    this.sessionForIdle = interval(intervalDuration).pipe(
      map((tick: number) => {
        return tick;
      }),
      takeWhile(() => SessionExpirationService.runTimer)
    );

    this.check();
  }

  private check(): void {
    if (!this.sessionForIdle) return;

    this.sessionForIdle.subscribe(() => {
      const now = Date.now();
      const validUntil = this.lastAction + this.USER_IDLE_TIMER_VALUE_IN_MIN * 60 * 1000;
      const diff = validUntil - now;
      const isTimeout = diff < 0;
      this.userIdlenessChecker!.next(`${diff}`);

      if (isTimeout) {
        window.document.removeEventListener('click', this.userActivityChangeCallback, true);

        this.zone.run(() => {
          if (this.userIdlenessChecker) {
            this.userIdlenessChecker.next(SessionExpirationStatus.StopExpirationTimer);

            if (this.WARNING_TIMER_VALUE_IN_MIN > 0) {
              this.idlenessWarningChecker!.next(SessionExpirationStatus.StartWarningTimer);
              this.executeFinalTimer();
            }
          }
          SessionExpirationService.runTimer = false;
        });
      }
    });
  }

  public removeActionFromStore(): void {
    localStorage.removeItem(STORE_KEY);
  }

  private executeFinalTimer = () => {
    SessionExpirationService.runWarningTimer = true;
    this.initializeFinalTimer();
  };

  private initializeFinalTimer(): void {
    const intervalDuration = 1000;

    this.clockForIdle = interval(intervalDuration).pipe(
      map((tick: number) => {
        return tick;
      }),
      takeWhile(() => SessionExpirationService.runWarningTimer)
    );

    this.checkUserActionTime();
  }

  private checkUserActionTime(): void {
    if (!this.clockForIdle) return;

    let timeInSecond = 60;
    let timeInMin = this.WARNING_TIMER_VALUE_IN_MIN - 1;
    this.clockForIdle.subscribe(() => {
      if (--timeInSecond === 0) {
        if (--timeInMin === 0) {
          timeInMin = timeInMin > 0 ? timeInMin - 1 : 0;
        }

        if (timeInMin === -1 && timeInSecond === 0) {
          SessionExpirationService.runWarningTimer = false;

          if (this.idlenessWarningChecker) {
            this.idlenessWarningChecker.next(SessionExpirationStatus.StopWarningTimer);
          }
        }

        if (timeInMin < 0) {
          timeInMin = 0;
          setTimeout(() => {
            timeInSecond = 60;
          }, 800);
        } else {
          timeInSecond = 60;
        }
      }

      const timeLeftInMs = timeInMin * 60 * 1000 + timeInSecond * 1000;
      this.idlenessWarningChecker!.next(timeLeftInMs.toString());
    });
  }

  ngOnDestroy(): void {
    if (this.userIdlenessChecker) {
      this.userIdlenessChecker.unsubscribe();
      this.userIdlenessChecker = undefined;
    }

    if (this.idlenessWarningChecker) {
      this.idlenessWarningChecker.unsubscribe();
      this.idlenessWarningChecker = undefined;
    }
  }
}
